import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, TrendingUp, Target, FileSearch, Radar, Settings2, Copy, Sparkles,
  Link2, AlertTriangle, CheckCircle2,
} from "lucide-react";
import {
  adminMarketingOverview, adminMarketingCampaigns, adminUpdateCampaignStatus,
  adminGetMarketingSettings, adminUpdateMarketingSettings,
} from "@/lib/api";
import { CAMPAIGNS, RETARGETING_AUDIENCES } from "@/data/campaigns";
import { campaignFinalUrl } from "@/lib/utm";

const CH_LABEL = { ai: "AI Search", organic: "Organic", paid: "Paid", social: "Social", referral: "Referral", direct: "Direct" };
const CH_COLOR = {
  ai: "text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-400/10",
  organic: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  paid: "text-cyan-300 border-cyan-400/30 bg-cyan-400/10",
  social: "text-sky-300 border-sky-400/30 bg-sky-400/10",
  referral: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  direct: "text-zinc-300 border-zinc-500/30 bg-zinc-500/10",
};
const money = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);
const card = "rounded-2xl border border-[#191919] bg-[#0a0a0a] p-6";
const mono = "mono-label text-zinc-500 text-xs";

const StatusChip = ({ s }) => {
  const map = {
    ACTIVE: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
    PAUSED: "text-amber-300 border-amber-400/30 bg-amber-400/10",
    DRAFT: "text-sky-300 border-sky-400/30 bg-sky-400/10",
    PLANNED: "text-zinc-300 border-zinc-500/30 bg-zinc-500/10",
  };
  return <span className={`rounded-full border px-2.5 py-0.5 text-[10px] mono-label ${map[s] || map.PLANNED}`}>{s}</span>;
};
const NotConnected = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/25 bg-amber-400/[0.06] px-2.5 py-0.5 text-[10px] mono-label text-amber-300">
    <AlertTriangle className="h-3 w-3" /> NOT CONNECTED
  </span>
);

function FunnelBar({ stage, value, max }) {
  const pct = max ? Math.max(3, Math.round((value / max) * 100)) : 3;
  return (
    <div data-testid={`funnel-stage-${stage.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center gap-4">
      <div className="w-48 shrink-0 text-sm text-zinc-400">{stage}</div>
      <div className="flex-1 h-8 rounded-lg bg-[#0e0e0e] overflow-hidden">
        <div className="h-full rounded-lg bg-gradient-to-r from-cyan-500/70 to-cyan-400/30 flex items-center px-3" style={{ width: `${pct}%` }}>
          <span className="text-xs font-semibold text-white">{value}</span>
        </div>
      </div>
    </div>
  );
}

function Overview({ data }) {
  const max = Math.max(...data.funnel.map((f) => f.value), 1);
  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="growth-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ["Qualified leads", t.leads], ["Proposals", t.proposals], ["Deals", t.deals],
          ["Customers", t.customers], ["Revenue", money(t.revenue)],
          ["Q. starts", t.questionnaire_starts], ["Q. completions", t.questionnaire_completions],
          ["Completion rate", t.conversion_rate == null ? "—" : `${t.conversion_rate}%`],
        ].map(([k, v]) => (
          <div key={k} className={card}>
            <div className={mono}>{k}</div>
            <div className="mt-2 text-2xl font-bold text-white">{v}</div>
          </div>
        ))}
      </div>
      <div className={card}>
        <div className="flex items-center gap-2 mb-6"><TrendingUp className="h-4 w-4 text-cyan-400" /><span className={mono}>ACQUISITION FUNNEL</span></div>
        <div className="space-y-3">{data.funnel.map((f) => <FunnelBar key={f.stage} {...f} max={max} />)}</div>
        <p className="mt-5 text-xs text-zinc-600">Counts are from AIROTECH's own database. Tracked funnel stages (service views, questionnaire) began with analytics rollout; historical leads/proposals/deals predate it.</p>
      </div>
    </div>
  );
}

function Attribution({ data }) {
  const rows = ["ai", "organic", "paid", "social", "referral", "direct"];
  return (
    <div className="space-y-6" data-testid="growth-attribution">
      <div className={`${card} border-fuchsia-400/20`}>
        <div className="flex items-center gap-2 mb-1"><Sparkles className="h-4 w-4 text-fuchsia-300" /><span className={mono}>AI-SOURCED LEADS</span></div>
        <div className="text-3xl font-bold text-white">{data.leads_by_channel_first_touch.ai || 0}</div>
        <p className="mt-1 text-xs text-zinc-500">Leads whose first touch came from an AI answer engine (ChatGPT, Perplexity, Copilot, Gemini, Claude). A fast-growing channel tracked as first-class.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className={card}>
          <div className={`${mono} mb-4`}>LEADS BY CHANNEL — FIRST TOUCH</div>
          <div className="space-y-2">
            {rows.map((c) => (
              <div key={c} className="flex items-center justify-between">
                <span className={`rounded-full border px-2.5 py-0.5 text-[10px] mono-label ${CH_COLOR[c]}`}>{CH_LABEL[c]}</span>
                <span className="text-white font-semibold" data-testid={`ft-${c}`}>{data.leads_by_channel_first_touch[c] || 0}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={card}>
          <div className={`${mono} mb-4`}>LEADS BY CHANNEL — LAST TOUCH</div>
          <div className="space-y-2">
            {rows.map((c) => (
              <div key={c} className="flex items-center justify-between">
                <span className={`rounded-full border px-2.5 py-0.5 text-[10px] mono-label ${CH_COLOR[c]}`}>{CH_LABEL[c]}</span>
                <span className="text-white font-semibold" data-testid={`lt-${c}`}>{data.leads_by_channel_last_touch[c] || 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={card}>
        <div className={`${mono} mb-4`}>REVENUE BY CHANNEL</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {rows.map((c) => (
            <div key={c} className="flex items-center justify-between rounded-lg border border-[#161616] px-4 py-3">
              <span className={`rounded-full border px-2.5 py-0.5 text-[10px] mono-label ${CH_COLOR[c]}`}>{CH_LABEL[c]}</span>
              <span className="text-white font-semibold">{money(data.revenue_by_channel[c])}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={card}>
        <div className={`${mono} mb-4`}>TOP LANDING PAGES</div>
        {data.top_pages.length === 0 ? <p className="text-sm text-zinc-600">No tracked page data yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-zinc-600 mono-label text-xs"><th className="pb-2">Page</th><th className="pb-2 text-right">Views</th><th className="pb-2 text-right">Leads</th></tr></thead>
            <tbody>
              {data.top_pages.map((p) => (
                <tr key={p.page} className="border-t border-[#141414]"><td className="py-2 text-zinc-300">{p.page}</td><td className="py-2 text-right text-zinc-400">{p.views}</td><td className="py-2 text-right text-white font-semibold">{p.leads}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ContentPriority({ data }) {
  return (
    <div className={card} data-testid="growth-content-priority">
      <div className="flex items-center gap-2 mb-2"><FileSearch className="h-4 w-4 text-cyan-400" /><span className={mono}>SEO / CONTENT PRIORITIZATION</span></div>
      <p className="text-xs text-zinc-600 mb-5">Scored on search intent, commercial + service relevance, observed opportunity, conversion potential and existing authority. Prioritize pages capable of producing customers.</p>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-zinc-600 mono-label text-xs">
          <th className="pb-2">Page</th><th className="pb-2">Service</th><th className="pb-2 text-right">Views</th><th className="pb-2 text-right">Leads</th><th className="pb-2 text-right">Priority</th>
        </tr></thead>
        <tbody>
          {data.content_priorities.map((p) => (
            <tr key={p.slug} className="border-t border-[#141414]" data-testid={`priority-${p.slug}`}>
              <td className="py-2 text-zinc-300">/{p.slug}</td>
              <td className="py-2 text-zinc-500">{p.service}</td>
              <td className="py-2 text-right text-zinc-400">{p.views}</td>
              <td className="py-2 text-right text-zinc-400">{p.leads}</td>
              <td className="py-2 text-right">
                <span className="inline-block w-12 text-white font-semibold">{p.score}</span>
                <span className="inline-block ml-2 h-1.5 w-16 rounded-full bg-[#141414] align-middle overflow-hidden">
                  <span className="block h-full bg-cyan-400" style={{ width: `${p.score}%` }} />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Campaigns({ rows, onStatus }) {
  const metaById = Object.fromEntries(CAMPAIGNS.map((c) => [c.id, c]));
  const copy = (url) => { navigator.clipboard?.writeText(url); toast.success("Final URL copied"); };
  return (
    <div className="space-y-5" data-testid="growth-campaigns">
      <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.05] p-4 text-sm text-amber-200/90 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>Campaign <b>architecture</b> only. No ads are live. Campaigns stay <b>PLANNED</b> until real Google Ads credentials are connected — activation is blocked until then.</span>
      </div>
      {rows.map((c) => {
        const meta = metaById[c.id] || {};
        const url = campaignFinalUrl(c);
        const keywords = (meta.adGroups || []).flatMap((g) => g.keywords).slice(0, 6);
        return (
          <div key={c.id} className={card} data-testid={`campaign-${c.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3"><span className="font-semibold text-white">{c.name}</span><StatusChip s={c.status} /></div>
                <div className="mt-1 text-xs text-zinc-500">→ /{c.landing_slug} · conversion: START YOUR BUILD (lead_created)</div>
              </div>
              <div className="flex gap-1.5">
                {["PLANNED", "DRAFT", "ACTIVE", "PAUSED"].map((s) => (
                  <button key={s} data-testid={`campaign-${c.id}-status-${s.toLowerCase()}`} onClick={() => onStatus(c.id, s)}
                    className={`rounded-md px-2 py-1 text-[10px] mono-label border transition-colors ${c.status === s ? "border-cyan-400/50 text-cyan-300 bg-cyan-400/10" : "border-[#232323] text-zinc-500 hover:text-white"}`}>{s}</button>
                ))}
              </div>
            </div>
            {keywords.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {keywords.map((k) => <span key={k} className="rounded-full border border-[#232323] px-2.5 py-0.5 text-[10px] text-zinc-400">{k}</span>)}
              </div>
            )}
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-[#161616] bg-[#0c0c0c] px-3 py-2">
              <Link2 className="h-3.5 w-3.5 text-zinc-600 shrink-0" />
              <code className="flex-1 truncate text-xs text-zinc-400">{url}</code>
              <button onClick={() => copy(url)} data-testid={`campaign-${c.id}-copy-url`} className="text-zinc-500 hover:text-cyan-300"><Copy className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Retargeting({ settings }) {
  return (
    <div className="space-y-5" data-testid="growth-retargeting">
      <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.05] p-4 text-sm text-amber-200/90 flex items-start gap-2">
        <Radar className="h-4 w-4 mt-0.5 shrink-0" />
        <span>Retargeting is <b>{settings?.retargeting_enabled ? "ENABLED" : "DISABLED"}</b> and consent-gated. Third-party pixels only load with both a configured pixel ID and visitor consent. No pixel IDs are configured, so nothing loads.</span>
      </div>
      <div className={card}>
        <div className={`${mono} mb-4`}>AUDIENCE DEFINITIONS (INACTIVE)</div>
        <div className="space-y-3">
          {RETARGETING_AUDIENCES.map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded-lg border border-[#161616] px-4 py-3">
              <div>
                <div className="text-sm text-white flex items-center gap-2">{a.name}{a.priority && <span className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2 py-0.5 text-[9px] mono-label text-cyan-300">HIGH PRIORITY</span>}</div>
                <div className="text-xs text-zinc-500">{a.definition}</div>
              </div>
              <span className="text-[10px] mono-label text-zinc-600 uppercase">{a.stage}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SettingsTab({ settings, onSave, saving }) {
  const [local, setLocal] = useState(settings);
  useEffect(() => setLocal(settings), [settings]);
  const integrations = settings?.integrations || {};
  const toggle = (k) => setLocal((s) => ({ ...s, [k]: !s[k] }));
  return (
    <div className="space-y-6" data-testid="growth-settings">
      <div className={card}>
        <div className="flex items-center gap-2 mb-4"><Settings2 className="h-4 w-4 text-cyan-400" /><span className={mono}>TRACKING & CONSENT</span></div>
        {[
          ["first_party_tracking_enabled", "First-party analytics (attribution + funnel)", "Our own tracking to AIROTECH's backend. Essential."],
          ["consent_required", "Require consent for marketing cookies", "Show a consent prompt before any third-party pixel loads."],
          ["retargeting_enabled", "Enable retargeting pixels", "Master switch. Pixels still require IDs + consent to load."],
        ].map(([k, label, desc]) => (
          <label key={k} className="flex items-center justify-between py-3 border-t border-[#141414] first:border-0 cursor-pointer">
            <div><div className="text-sm text-white">{label}</div><div className="text-xs text-zinc-500">{desc}</div></div>
            <button type="button" data-testid={`toggle-${k}`} onClick={() => toggle(k)}
              className={`relative h-6 w-11 rounded-full transition-colors ${local?.[k] ? "bg-cyan-500" : "bg-[#2a2a2a]"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${local?.[k] ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </label>
        ))}
        <button data-testid="growth-save-settings" onClick={() => onSave({ first_party_tracking_enabled: local.first_party_tracking_enabled, consent_required: local.consent_required, retargeting_enabled: local.retargeting_enabled })}
          disabled={saving} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-cyan-500 px-4 py-2 text-sm font-semibold text-black hover:bg-cyan-400 transition-colors disabled:opacity-50">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save settings
        </button>
      </div>
      <div className={card}>
        <div className={`${mono} mb-4`}>EXTERNAL INTEGRATIONS</div>
        <p className="text-xs text-zinc-600 mb-4">Impressions, clicks, ad spend and search positions require these connections. All are shown as NOT CONNECTED until real credentials are configured — no external numbers are fabricated.</p>
        <div className="space-y-2">
          {[["ga4", "Google Analytics 4"], ["google_search_console", "Google Search Console"], ["bing_webmaster", "Bing Webmaster"], ["google_ads", "Google Ads"], ["meta_ads", "Meta Ads"]].map(([k, label]) => (
            <div key={k} className="flex items-center justify-between rounded-lg border border-[#161616] px-4 py-3">
              <span className="text-sm text-zinc-300">{label}</span>
              {integrations[k] === "connected" ? <span className="inline-flex items-center gap-1.5 text-[10px] mono-label text-emerald-300"><CheckCircle2 className="h-3 w-3" /> CONNECTED</span> : <NotConnected />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const SUBTABS = [
  ["overview", "Funnel", TrendingUp],
  ["attribution", "Attribution", Target],
  ["content", "Content Priority", FileSearch],
  ["campaigns", "Campaigns", Sparkles],
  ["retargeting", "Retargeting", Radar],
  ["settings", "Settings", Settings2],
];

export default function GrowthCenter() {
  const [sub, setSub] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [o, c, s] = await Promise.all([adminMarketingOverview(), adminMarketingCampaigns(), adminGetMarketingSettings()]);
      setOverview(o); setCampaigns(c.campaigns); setSettings(s);
    } catch (e) { toast.error("Could not load Growth data"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onStatus = async (id, status) => {
    try {
      await adminUpdateCampaignStatus(id, status);
      setCampaigns((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Campaign set to ${status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const onSaveSettings = async (payload) => {
    setSaving(true);
    try { const s = await adminUpdateMarketingSettings(payload); setSettings(s); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };

  if (loading || !overview) return <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>;

  return (
    <div data-testid="growth-center">
      <div className="mb-6">
        <h2 className="font-heading text-2xl text-white">Growth Command Center</h2>
        <p className="text-sm text-zinc-500 mt-1">SEO · AEO · Paid Search · Content · Conversions · Attribution — from your own data.</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-8 border-b border-[#161616] pb-4">
        {SUBTABS.map(([k, label, Icon]) => (
          <button key={k} data-testid={`growth-subtab-${k}`} onClick={() => setSub(k)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 mono-label text-xs transition-colors ${sub === k ? "bg-cyan-400/10 text-cyan-300" : "text-zinc-500 hover:text-white"}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>
      {sub === "overview" && <Overview data={overview} />}
      {sub === "attribution" && <Attribution data={overview} />}
      {sub === "content" && <ContentPriority data={overview} />}
      {sub === "campaigns" && <Campaigns rows={campaigns} onStatus={onStatus} />}
      {sub === "retargeting" && <Retargeting settings={settings} />}
      {sub === "settings" && <SettingsTab settings={settings} onSave={onSaveSettings} saving={saving} />}
    </div>
  );
}
